import React, { useState, useEffect } from 'react';
import { Field, Input, Select, Text } from '@fluentui/react-components';
import { 
  LLMProvider,
  LLMProviderConfig,
  getProviderDisplayName,
  DEFAULT_LLM_CONFIG
} from '../../types/llmSettings';

interface LLMProviderFormProps {
  mode: 'add' | 'edit';
  initialConfig?: Partial<LLMProviderConfig>;
  onValidationChange?: (isValid: boolean, errors: string[]) => void;
  onChange?: (config: Partial<LLMProviderConfig>, hasChanges: boolean) => void;
  disabled?: boolean;
  testIdPrefix?: string;
  includeApiKey?: boolean; // Allow API key to be pre-populated (for duplication)
}

export const LLMProviderForm: React.FC<LLMProviderFormProps> = ({
  mode,
  initialConfig,
  onValidationChange,
  onChange,
  disabled = false,
  testIdPrefix = 'modal',
  includeApiKey = false
}) => {
  const [formProvider, setFormProvider] = useState<LLMProvider | ''>(
    initialConfig?.provider || ''
  );
  
  const [formData, setFormData] = useState(() => {
    const baseData = {
      displayName: initialConfig?.displayName || '',
      apiKey: includeApiKey ? ((initialConfig as { apiKey?: string })?.apiKey || '') : '', // Populate API key only when explicitly requested
      model: initialConfig?.model || '',
      temperature: (initialConfig?.temperature ?? DEFAULT_LLM_CONFIG.temperature).toString(),
      maxTokens: (initialConfig?.maxTokens ?? DEFAULT_LLM_CONFIG.maxTokens).toString(),
      timeoutSeconds: (initialConfig?.timeoutSeconds ?? DEFAULT_LLM_CONFIG.timeoutSeconds).toString(),
      endpoint: '',
      deploymentName: '',
      baseUrl: '',
      organizationId: ''
    };

    // Populate provider-specific fields from API data immediately
    if (initialConfig) {
      if (initialConfig.provider === LLMProvider.AZURE_OPENAI) {
        const azureConfig = initialConfig as {endpoint?: string; deploymentName?: string};
        baseData.endpoint = azureConfig.endpoint || '';
        baseData.deploymentName = azureConfig.deploymentName || '';
      } else if (initialConfig.provider === LLMProvider.OPENAI) {
        const openaiConfig = initialConfig as {baseUrl?: string; organizationId?: string};
        baseData.baseUrl = openaiConfig.baseUrl || '';
        baseData.organizationId = openaiConfig.organizationId || '';
      } else if (initialConfig.provider === LLMProvider.OLLAMA) {
        const ollamaConfig = initialConfig as {baseUrl?: string};
        baseData.baseUrl = ollamaConfig.baseUrl || '';
      }
    }

    return baseData;
  });

  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [_hasChanges, setHasChanges] = useState(false);

  // Initialize form state from initialConfig when it changes
  useEffect(() => {
    if (initialConfig?.provider) {
      setFormProvider(initialConfig.provider);
      
      // Re-initialize form data when initialConfig changes
      const baseData = {
        displayName: initialConfig.displayName || '',
        apiKey: includeApiKey ? ((initialConfig as { apiKey?: string }).apiKey || '') : '', // Populate API key only when explicitly requested
        model: initialConfig.model || '',
        temperature: (initialConfig.temperature ?? DEFAULT_LLM_CONFIG.temperature).toString(),
        maxTokens: (initialConfig.maxTokens ?? DEFAULT_LLM_CONFIG.maxTokens).toString(),
        timeoutSeconds: (initialConfig.timeoutSeconds ?? DEFAULT_LLM_CONFIG.timeoutSeconds).toString(),
        endpoint: '',
        deploymentName: '',
        baseUrl: '',
        organizationId: ''
      };

      // Populate provider-specific fields from API data
      if (initialConfig.provider === LLMProvider.AZURE_OPENAI) {
        const azureConfig = initialConfig as {endpoint?: string; deploymentName?: string};
        baseData.endpoint = azureConfig.endpoint || '';
        baseData.deploymentName = azureConfig.deploymentName || '';
      } else if (initialConfig.provider === LLMProvider.OPENAI) {
        const openaiConfig = initialConfig as {baseUrl?: string; organizationId?: string};
        baseData.baseUrl = openaiConfig.baseUrl || '';
        baseData.organizationId = openaiConfig.organizationId || '';
      } else if (initialConfig.provider === LLMProvider.OLLAMA) {
        const ollamaConfig = initialConfig as {baseUrl?: string};
        baseData.baseUrl = ollamaConfig.baseUrl || '';
      }

      setFormData(baseData);
    }
  }, [initialConfig, includeApiKey]);

  // Get masked API key for edit mode placeholder
  const getMaskedApiKey = (): string => {
    if (mode === 'edit' && initialConfig?.provider !== LLMProvider.OLLAMA) {
      const apiKey = (initialConfig as {apiKey?: string})?.apiKey;
      if (apiKey && apiKey.length > 4) {
        const last4 = apiKey.slice(-4);
        return `••••••••${last4}`;
      }
    }
    return '';
  };

  const handleFormFieldChange = (field: string) => (
    _event: unknown, 
    data: { value: string }
  ) => {
    const newFormData = { ...formData, [field]: data.value };
    setFormData(newFormData);
    setHasChanges(true);
    
    // Clear errors when user starts typing
    if (formErrors.length > 0) {
      validateForm(formProvider, newFormData, false);
    }

    // Notify parent of changes
    if (onChange) {
      const config = buildConfig(formProvider, newFormData);
      onChange(config, true);
    }
  };

  const handleProviderTypeChange = (_event: unknown, data: { value: string }) => {
    const provider = data.value as LLMProvider;
    setFormProvider(provider);
    setHasChanges(true);
    
    // Clear errors when provider changes
    if (formErrors.length > 0) {
      validateForm(provider, formData, false);
    }

    // Notify parent of changes
    if (onChange) {
      const config = buildConfig(provider, formData);
      onChange(config, true);
    }
  };

  const buildConfig = (provider: LLMProvider | '', data: typeof formData): Partial<LLMProviderConfig> => {
    if (!provider) return {};

    const baseConfig: Partial<LLMProviderConfig> = {
      provider,
      displayName: data.displayName,
      model: data.model,
      temperature: parseFloat(data.temperature) || DEFAULT_LLM_CONFIG.temperature,
      maxTokens: parseInt(data.maxTokens) || DEFAULT_LLM_CONFIG.maxTokens,
      timeoutSeconds: parseInt(data.timeoutSeconds) || DEFAULT_LLM_CONFIG.timeoutSeconds,
    };

    // Add provider-specific fields
    if (provider === LLMProvider.AZURE_OPENAI) {
      return {
        ...baseConfig,
        // Send empty string for API key to preserve existing key (backend expects this)
        apiKey: data.apiKey || "",
        endpoint: data.endpoint,
        deploymentName: data.deploymentName
      } as Partial<LLMProviderConfig>;
    } else if (provider === LLMProvider.OPENAI) {
      return {
        ...baseConfig,
        // Send empty string for API key to preserve existing key (backend expects this)
        apiKey: data.apiKey || "",
        baseUrl: data.baseUrl || undefined,
        organizationId: data.organizationId || undefined
      } as Partial<LLMProviderConfig>;
    } else if (provider === LLMProvider.ANTHROPIC) {
      return {
        ...baseConfig,
        // Send empty string for API key to preserve existing key (backend expects this)
        apiKey: data.apiKey || ""
      } as Partial<LLMProviderConfig>;
    } else if (provider === LLMProvider.OLLAMA) {
      return {
        ...baseConfig,
        baseUrl: data.baseUrl || undefined
      } as Partial<LLMProviderConfig>;
    }

    return baseConfig;
  };

  const validateForm = (
    provider: LLMProvider | '', 
    data: typeof formData, 
    updateState: boolean = true
  ): boolean => {
    const errors: string[] = [];
    
    if (!data.displayName.trim()) {
      errors.push('Display name is required');
    }
    
    if (!provider) {
      errors.push('Provider type is required');
    } else {
      // In edit mode, API key is optional (empty means keep existing)
      if (mode === 'add' && provider !== LLMProvider.OLLAMA && !data.apiKey.trim()) {
        errors.push('API key is required');
      }
      
      if (provider === LLMProvider.AZURE_OPENAI) {
        if (!data.endpoint.trim()) errors.push('Endpoint is required for Azure OpenAI');
        if (!data.deploymentName.trim()) errors.push('Deployment name is required for Azure OpenAI');
      }
    }
    
    if (!data.model.trim()) {
      errors.push('Model is required');
    }
    
    if (updateState) {
      setFormErrors(errors);
    }
    
    if (onValidationChange) {
      onValidationChange(errors.length === 0, errors);
    }
    
    return errors.length === 0;
  };

  // Expose validation method through useEffect
  useEffect(() => {
    validateForm(formProvider, formData, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formProvider, formData]);

  return (
    <>
      <Field label="Provider Type">
        <Select 
          data-testid={`${testIdPrefix}-provider-type-dropdown`}
          value={formProvider}
          onChange={handleProviderTypeChange}
          disabled={disabled || mode === 'edit'} // Provider type cannot be changed in edit mode
        >
          <option value="">Select Provider Type</option>
          <option value={LLMProvider.AZURE_OPENAI}>{getProviderDisplayName(LLMProvider.AZURE_OPENAI)}</option>
          <option value={LLMProvider.OPENAI}>{getProviderDisplayName(LLMProvider.OPENAI)}</option>
          <option value={LLMProvider.ANTHROPIC}>{getProviderDisplayName(LLMProvider.ANTHROPIC)}</option>
          <option value={LLMProvider.OLLAMA}>{getProviderDisplayName(LLMProvider.OLLAMA)}</option>
        </Select>
      </Field>
      
      {formProvider && (
        <>
          <Field label="Display Name">
            <Input 
              data-testid={`${testIdPrefix}-field-displayName`}
              placeholder="Display Name" 
              value={formData.displayName}
              onChange={handleFormFieldChange('displayName')}
              disabled={disabled}
            />
          </Field>
          
          {formProvider !== LLMProvider.OLLAMA && (
            <Field label="API Key">
              <Input 
                data-testid={`${testIdPrefix}-field-apiKey`}
                placeholder={mode === 'edit' ? getMaskedApiKey() : 'API Key'}
                type="password"
                value={formData.apiKey}
                onChange={handleFormFieldChange('apiKey')}
                disabled={disabled}
              />
              {mode === 'edit' && (
                <>
                  {getMaskedApiKey() && (
                    <Text size={200} style={{ marginTop: '4px', fontWeight: 'semibold' }}>
                      Current: {getMaskedApiKey()}
                    </Text>
                  )}
                  <Text size={200} style={{ marginTop: '4px' }}>
                    Leave empty to keep existing API key
                  </Text>
                </>
              )}
            </Field>
          )}
          
          <Field label="Model">
            <Input 
              data-testid={`${testIdPrefix}-field-model`}
              placeholder="Model (e.g., gpt-4, claude-3-sonnet)" 
              value={formData.model}
              onChange={handleFormFieldChange('model')}
              disabled={disabled}
            />
          </Field>
          
          <Field label="Temperature">
            <Input 
              data-testid={`${testIdPrefix}-field-temperature`}
              placeholder="Temperature" 
              type="number" 
              step="0.1" 
              min="0" 
              max="2"
              value={formData.temperature}
              onChange={handleFormFieldChange('temperature')}
              disabled={disabled}
            />
          </Field>
          
          <Field label="Max Tokens">
            <Input 
              data-testid={`${testIdPrefix}-field-maxTokens`}
              placeholder="Max Tokens" 
              type="number"
              value={formData.maxTokens}
              onChange={handleFormFieldChange('maxTokens')}
              disabled={disabled}
            />
          </Field>
          
          <Field label="Timeout (seconds)">
            <Input 
              data-testid={`${testIdPrefix}-field-timeoutSeconds`}
              placeholder="Timeout in seconds" 
              type="number"
              min="10"
              max="300"
              value={formData.timeoutSeconds}
              onChange={handleFormFieldChange('timeoutSeconds')}
              disabled={disabled}
            />
          </Field>
          
          {formProvider === LLMProvider.AZURE_OPENAI && (
            <>
              <Field label="Endpoint">
                <Input 
                  data-testid={`${testIdPrefix}-field-endpoint`}
                  placeholder="https://your-resource.openai.azure.com/" 
                  value={formData.endpoint}
                  onChange={handleFormFieldChange('endpoint')}
                  disabled={disabled}
                />
              </Field>
              
              <Field label="Deployment Name">
                <Input 
                  data-testid={`${testIdPrefix}-field-deploymentName`}
                  placeholder="Deployment Name" 
                  value={formData.deploymentName}
                  onChange={handleFormFieldChange('deploymentName')}
                  disabled={disabled}
                />
              </Field>
            </>
          )}
          
          {formProvider === LLMProvider.OPENAI && (
            <>
              <Field label="Base URL (Optional)">
                <Input 
                  data-testid={`${testIdPrefix}-field-baseUrl`}
                  placeholder="https://api.openai.com/v1" 
                  value={formData.baseUrl}
                  onChange={handleFormFieldChange('baseUrl')}
                  disabled={disabled}
                />
              </Field>
              
              <Field label="Organization ID (Optional)">
                <Input 
                  data-testid={`${testIdPrefix}-field-organizationId`}
                  placeholder="Organization ID" 
                  value={formData.organizationId}
                  onChange={handleFormFieldChange('organizationId')}
                  disabled={disabled}
                />
              </Field>
            </>
          )}
          
          {formProvider === LLMProvider.OLLAMA && (
            <Field label="Base URL (Optional)">
              <Input 
                data-testid={`${testIdPrefix}-field-baseUrl`}
                placeholder="http://localhost:11434" 
                value={formData.baseUrl}
                onChange={handleFormFieldChange('baseUrl')}
                disabled={disabled}
              />
            </Field>
          )}
          
          {formErrors.map((error, index) => (
            <Text key={index} style={{ color: 'red', display: 'block', marginTop: '8px' }}>
              {error}
            </Text>
          ))}
        </>
      )}
    </>
  );
};

export default LLMProviderForm;